import React, { useEffect, useRef } from 'react';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import FormStepper from '../../Form/FormStepper/FormStepper';
import { useAccreditationApplicationFormComponent } from './hooks/useAccreditationApplicationFormComponent';
import IFormStep from 'interfaces/IFormStep';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '../../LoadingSpinner';
import { useNotificationContext } from '../../../contexts/NotificationContext';
import Prerequisites from './Prerequisites';
import { useAuth } from 'react-oidc-context';
import { ReviewerRole, ReviewStatus } from '../../../graphql/__generated__/graphql';

interface Props {
    renewal?: boolean;
}

const ApplicationForm: React.FC<Props> = ({ renewal = false }) => {
    const formRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();
    const notificationContext = useNotificationContext();
    const { models, operations } = useAccreditationApplicationFormComponent(
        location.state?.application
    );

    const { formData, currentStep, requirements, operationResult, application } = models;

    useEffect(() => {
        if (operationResult?.message) {
            notificationContext.notify(
                operationResult.success ? 'info' : 'danger',
                operationResult.message
            );
        }
    }, [operationResult]);

    if (!application) {
        return <LoadingSpinner />;
    }

    const {
        handleCanSkipToStep,
        handleNextStep,
        handleInputChangeEvent,
        handleInputChange,
        handleSaveProgress,
        handleSetStep,
        handleSubmitApplication,
        handleFetchApplication,
    } = operations;

    const handleSubmit = async () => {
        return handleSubmitApplication()
            .then(() => {
                navigate('/accreditation?formSubmitType=supervisor');
                return this;
            })
            .catch(() => {
                handleFetchApplication();
            });
    };

    const renderStep = (Step: React.FC<IFormStep>) => (
        <Step
            formData={formData}
            models={models}
            handleInputChangeEvent={handleInputChangeEvent}
            handleFeedback={notificationContext.notify}
            handleNext={handleNextStep}
            handleInputChange={handleInputChange}
            handleSubmitApplication={handleSubmit}
            handleFetchApplication={handleFetchApplication}
            requirements={requirements}
        />
    );

    const handleSaveButtonClicked = () => {
        return handleSaveProgress(formData)
            .then(() => {
                notificationContext.success('Application saved');
            })
            .catch(() => {
                notificationContext.error('An error occurred saving the application');
            });
    };

    const emdrReview = application.reviews?.find(
        (review) => review.reviewerRole === ReviewerRole.Emdr
    );

    return (
        <>
            <h1 className="fs-1 fw-bold mb-5">
                Apply for {application.accreditationType.name} {application.isRenewal && 're'}
                accreditation
            </h1>

            {emdrReview &&
                emdrReview.reviewStatus === ReviewStatus.WaitingOnApplicant &&
                emdrReview.jsonData && (
                    <Row>
                        <Col>
                            <Alert variant="danger">
                                <Alert.Heading>EMDR committee comments</Alert.Heading>
                                {JSON.parse(emdrReview.jsonData).comments}
                            </Alert>
                        </Col>
                    </Row>
                )}

            <Row>
                <Col>
                    {!application.accreditationApplicationId && (
                        <Prerequisites startForm={() => handleSetStep(1)} renewal={renewal} />
                    )}
                    {application.accreditationApplicationId && (
                        <Form ref={formRef} noValidate>
                            <FormStepper
                                stepNumber={currentStep}
                                renderStep={renderStep}
                                steps={models.stepsConfig}
                                formRef={formRef}
                                handleFeedback={notificationContext.notify}
                                handleCanSkipToStep={handleCanSkipToStep}
                                handleChangeStep={handleSetStep}
                                handleCancel={() => {
                                    navigate('/accreditation');
                                }}
                                handleSubmit={(e) => {
                                    // The application doesn't use a typical form submit
                                    e.preventDefault();
                                    e.stopPropagation();
                                }}
                                handleSaveProgress={handleSaveButtonClicked}
                                submitted={false}
                                submitting={false}
                                allowSubmit={false}
                                allowSkipStep={!!models.application.accreditationApplicationId}
                                submitText={'Submit and pay'}
                                nextButtonText={'Save and continue'}
                                className={'overflow-x-auto'}
                            ></FormStepper>
                        </Form>
                    )}
                </Col>
            </Row>
        </>
    );
};

export default ApplicationForm;
